import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface CreateProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  organizationId?: string; // Pre-fill when creating from PM detail page
}

export function CreateProductDialog({
  open,
  onOpenChange,
  onSuccess,
  organizationId,
}: CreateProductDialogProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    cardType: "VIRTUAL",
    cardNetwork: "RUPAY",
    currency: "INR",
    defaultDailyLimitCents: 50000,
    defaultMonthlyLimitCents: 200000,
    minLoadAmountCents: 100,
    maxLoadAmountCents: 20000000,
    bin: "",
    category: "",
    status: "ACTIVE",
    requestStatus: "DRAFT",
    organizationId: organizationId || "",
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/products", data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create product");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["pm-products"] });
      toast({
        title: "Success",
        description: "Product created successfully",
      });
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      cardType: "VIRTUAL",
      cardNetwork: "RUPAY",
      currency: "INR",
      defaultDailyLimitCents: 50000,
      defaultMonthlyLimitCents: 200000,
      minLoadAmountCents: 100,
      maxLoadAmountCents: 20000000,
      bin: "",
      category: "",
      status: "ACTIVE",
      requestStatus: "DRAFT",
      organizationId: organizationId || "",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Product</DialogTitle>
          <DialogDescription>Add a new card product/program</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Product Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Virtual Expense Card"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Employee expense card for daily business spending"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Card Type</Label>
                <Select
                  value={formData.cardType}
                  onValueChange={(v) =>
                    setFormData({ ...formData, cardType: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VIRTUAL">Virtual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Card Network</Label>
                <Select
                  value={formData.cardNetwork}
                  onValueChange={(v) =>
                    setFormData({ ...formData, cardNetwork: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VISA">Visa</SelectItem>
                    <SelectItem value="MASTERCARD">Mastercard</SelectItem>
                    <SelectItem value="RUPAY">RuPay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(v) => setFormData({ ...formData, currency: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INR">INR</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="GPR"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>BIN</Label>
              <Input
                value={formData.bin}
                onChange={(e) => setFormData({ ...formData, bin: e.target.value })}
                placeholder="81819410"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Daily Limit (paise/cents)</Label>
                <Input
                  type="number"
                  value={formData.defaultDailyLimitCents}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      defaultDailyLimitCents: parseInt(e.target.value),
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Monthly Limit (paise/cents)</Label>
                <Input
                  type="number"
                  value={formData.defaultMonthlyLimitCents}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      defaultMonthlyLimitCents: parseInt(e.target.value),
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Load (paise/cents)</Label>
                <Input
                  type="number"
                  value={formData.minLoadAmountCents}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minLoadAmountCents: parseInt(e.target.value),
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Max Load (paise/cents)</Label>
                <Input
                  type="number"
                  value={formData.maxLoadAmountCents}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxLoadAmountCents: parseInt(e.target.value),
                    })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
